import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MailCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { ASSETS } from "@/data/content";
import { Btn, Input } from "@/forum/ui";

export const Shell = ({ children, testId }) => (
  <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0a0a0b] px-5 text-white" data-testid={testId}>
    <img src={ASSETS.authBg} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
    <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0b]/60 via-[#0a0a0b]/80 to-[#0a0a0b]" />
    <div className="pointer-events-none absolute left-1/2 top-1/3 h-[32rem] w-[32rem] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(94,177,255,0.18),transparent_60%)] blur-3xl" />
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="relative w-full max-w-md">
      <Link to="/" className="mb-8 flex items-center justify-center gap-3">
        <img src={ASSETS.logo} alt="BCRP" className="h-12 w-12" />
        <span className="font-display text-sm font-bold uppercase tracking-[0.2em]">Bone County Roleplay</span>
      </Link>
      <div className="rounded-[2rem] border border-white/10 bg-[#111113]/80 p-8 backdrop-blur-xl sm:p-10">{children}</div>
    </motion.div>
  </div>
);

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (ex) { setErr(errorText(ex)); } finally { setBusy(false); }
  };

  return (
    <Shell testId="forgot-password-page">
      <p className="eyebrow mb-3">Fórum</p>
      <h1 className="font-display text-3xl font-bold">Elfelejtett jelszó</h1>
      {sent ? (
        <div className="mt-8 space-y-4" data-testid="forgot-sent">
          <span className="grid h-12 w-12 place-items-center rounded-full border border-[rgba(94,177,255,0.5)] text-[var(--glow)]"><MailCheck size={20} /></span>
          <p className="text-sm leading-relaxed text-white/70">Ha létezik fiók a(z) <strong className="text-white">{email}</strong> címmel, elküldtük a visszaállító linket. Nézd meg a bejövő leveleidet (és a spam mappát is). A link 1 óráig érvényes.</p>
          <Link to="/login" className="inline-block text-sm text-white underline-offset-4 hover:underline" data-testid="forgot-back-login">Vissza a belépéshez</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4">
          <p className="text-sm text-white/55">Add meg a fiókodhoz tartozó e-mail címet, és küldünk egy linket az új jelszó beállításához.</p>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-mail cím" required autoComplete="email" data-testid="forgot-email" />
          {err && <p className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-300" data-testid="forgot-error">{err}</p>}
          <Btn type="submit" disabled={busy} className="btn-glow w-full" data-testid="forgot-submit">{busy ? "Küldés…" : "Link küldése"}</Btn>
          <p className="text-center text-sm text-white/50"><Link to="/login" className="text-white underline-offset-4 hover:underline" data-testid="forgot-cancel">Vissza a belépéshez</Link></p>
        </form>
      )}
    </Shell>
  );
}

export function ResetPasswordPage() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return setErr("A két jelszó nem egyezik");
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/auth/reset-password", { token, password: pw });
      toast.success(data.message);
      nav("/login");
    } catch (ex) { setErr(errorText(ex)); } finally { setBusy(false); }
  };

  return (
    <Shell testId="reset-password-page">
      <p className="eyebrow mb-3">Fórum</p>
      <h1 className="font-display text-3xl font-bold">Új jelszó</h1>
      {!token ? (
        <p className="mt-6 text-sm text-white/60" data-testid="reset-no-token">Hiányzó vagy hibás link. <Link to="/forgot-password" className="text-white underline-offset-4 hover:underline">Kérj újat</Link>.</p>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4">
          <span className="grid h-12 w-12 place-items-center rounded-full border border-white/10 text-white/70"><KeyRound size={18} /></span>
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Új jelszó (min. 6 karakter)" required minLength={6} autoComplete="new-password" data-testid="reset-password" />
          <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Új jelszó még egyszer" required minLength={6} autoComplete="new-password" data-testid="reset-password-confirm" />
          {err && <p className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-300" data-testid="reset-error">{err}</p>}
          <Btn type="submit" disabled={busy} className="btn-glow w-full" data-testid="reset-submit">{busy ? "Mentés…" : "Jelszó mentése"}</Btn>
        </form>
      )}
    </Shell>
  );
}
